import React, { useMemo, useState } from "react";
import "../../styles/raci-matrix.css";
import SkillMatchModal from "./SkillMatchModal";
import { getJobTaskSkills } from "../../services/jobTaskSkillService";

const RACI_ROLES = ["R", "A", "C", "I"];

const ROLE_META = {
  R: { label: "Responsible", description: "does the work" },
  A: { label: "Accountable", description: "owns outcome" },
  C: { label: "Consulted", description: "input needed" },
  I: { label: "Informed", description: "kept updated" },
};

const RACI_DEFAULT_ALLOCATION = { R: 100, A: 25, C: 12, I: 5 };

// Mirrors the backend rule (only enforced there when `raci` is sent non-empty):
// exactly one Accountable, at least one Responsible. Also rejects unassigned rows.
export const getRaciValidation = (rows = []) => {
  const messages = [];
  const nullCount = rows.filter((r) => !r.role).length;
  if (nullCount > 0)
    messages.push(`${nullCount} job(s) have no RACI role assigned.`);
  const accountableCount = rows.filter((r) => r.role === "A").length;
  const responsibleCount = rows.filter((r) => r.role === "R").length;
  if (accountableCount === 0) {
    messages.push("Assign exactly one Accountable (A) job.");
  } else if (accountableCount > 1) {
    messages.push("Only one job can be marked Accountable (A).");
  }
  if (responsibleCount === 0) {
    messages.push("At least one Responsible (R) job is required.");
  }
  return { valid: messages.length === 0, messages };
};

const SummaryHeader = () => (
  <tr>
    <th className="raci-summary-th-task">Task</th>
    {RACI_ROLES.map((role) => (
      <th key={role} className="raci-summary-th-role">
        <div className="raci-summary-role-header">
          <span className={`raci-legend-badge raci-${role.toLowerCase()}`}>
            {role}
          </span>
          <span className="raci-summary-role-label">{ROLE_META[role].label}</span>
        </div>
      </th>
    ))}
  </tr>
);

const EmptyCell = () => <span className="raci-summary-empty">—</span>;

const RaciSummaryTable = ({ rows, itemById, idKey, getName, isTaskEntity, label }) => {
  const visibleRows = rows.filter((r) => itemById.has(String(r[idKey])));
  if (visibleRows.length === 0) return null;

  if (isTaskEntity) {
    return (
      <div className="raci-summary-wrap">
        <div className="raci-summary-title">RACI Overview</div>
        <table className="raci-summary-table">
          <thead><SummaryHeader /></thead>
          <tbody>
            {visibleRows.map((r) => {
              const it = itemById.get(String(r[idKey]));
              return (
                <tr key={r[idKey]}>
                  <td>{getName(it) || "—"}</td>
                  {RACI_ROLES.map((role) => (
                    <td key={role} className="raci-summary-role-cell">
                      {r.role === role ? (
                        <span className={`raci-badge raci-${role.toLowerCase()}`}>
                          {role}
                        </span>
                      ) : (
                        <EmptyCell />
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // entityType="job": rows are jobs for a task — group names by role
  const byRole = { R: [], A: [], C: [], I: [] };
  visibleRows.forEach((r) => {
    if (r.role && byRole[r.role]) {
      byRole[r.role].push(getName(itemById.get(String(r[idKey]))) || "—");
    }
  });
  return (
    <div className="raci-summary-wrap">
      <div className="raci-summary-title">RACI Overview</div>
      <table className="raci-summary-table">
        <thead><SummaryHeader /></thead>
        <tbody>
          <tr>
            <td>{label || "—"}</td>
            {RACI_ROLES.map((role) => (
              <td key={role} className="raci-summary-role-cell">
                {byRole[role].length > 0 ? (
                  byRole[role].join(", ")
                ) : (
                  <EmptyCell />
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/**
 * RaciMatrix
 * - mode: 'manage' (add/remove rows, assign roles) | 'view' (read-only)
 * - entityType: 'job' (rows are jobs-for-a-task, default) | 'task' (rows are
 *   tasks-for-a-job) — picks which id field/labels/pool prop to use.
 * - jobs: pool of selectable jobs [{ job_id, name, jobCode|job_code }] — used
 *   when entityType is 'job'
 * - tasks: pool of selectable tasks [{ task_id, task_name|name, task_code|code }]
 *   — used when entityType is 'task'
 * - rows: [{ job_id|task_id, role: 'R'|'A'|'C'|'I'|null }]
 * - onChange(rows): required in manage mode
 */
const RaciMatrix = ({
  mode = "manage",
  entityType = "job",
  jobs = [],
  tasks = [],
  rows = [],
  onChange,
  label,
  // Self entity ids — used to run the /job-task-skills check before a link is
  // committed. Pass jobId when entityType="task" (self is a job) and taskId
  // when entityType="job" (self is a task). Only available on Edit pages.
  jobId = null,
  taskId = null,
  // When true (Create pages, where the self entity isn't saved yet), the add
  // controls are disabled and an info bar prompts the user to save first.
  disabled = false,
  disabledMessage = "Save this record first, then link jobs and tasks in the RACI matrix.",
}) => {
  const isTaskEntity = entityType === "task";
  const idKey = isTaskEntity ? "task_id" : "job_id";
  const items = isTaskEntity ? tasks : jobs;
  const getName = (it) =>
    isTaskEntity ? it?.task_name || it?.name : it?.name;
  const getCode = (it) =>
    isTaskEntity ? it?.task_code || it?.code : it?.jobCode || it?.job_code;
  const nameColumnLabel = isTaskEntity ? "Task Name" : "Job Name";
  const codeColumnLabel = isTaskEntity ? "Task Code" : "Job Code";
  const addLabel = isTaskEntity ? "Add task to matrix" : "Add job to matrix";
  const selectPlaceholder =
    isTaskEntity ? "Select task to add" : "Select job to add";
  const emptyLabel =
    isTaskEntity ? "No tasks added to the matrix" : "No jobs added to the matrix";

  const [itemToAdd, setItemToAdd] = useState("");
  const isManage = mode === "manage";

  // Skill-match confirm-before-linking modal state.
  const [skillModal, setSkillModal] = useState({
    open: false,
    loading: false,
    error: null,
    data: null,
    addId: null,
    jobName: "",
    taskName: "",
  });

  const itemById = useMemo(() => {
    const m = new Map();
    (items || []).forEach((it) => m.set(String(it[idKey]), it));
    return m;
  }, [items, idKey]);

  const availableItems = useMemo(() => {
    const used = new Set(rows.map((r) => String(r[idKey])));
    return (items || []).filter((it) => !used.has(String(it[idKey])));
  }, [items, rows, idKey]);

  const visibleRows = rows.filter((r) => itemById.has(String(r[idKey])));

  // Per-task invariant: exactly one Accountable, at least one Responsible.
  // For task-entity mode we only check null roles — the cross-job rule does
  // not apply because other jobs may hold A/R for the same task.
  const validation = useMemo(() => {
    if (isTaskEntity) {
      const nullCount = visibleRows.filter((r) => !r.role).length;
      if (nullCount > 0)
        return {
          valid: false,
          messages: [`${nullCount} task(s) have no RACI role assigned.`],
        };
      return { valid: true, messages: [] };
    }
    return getRaciValidation(rows);
  }, [rows, visibleRows, isTaskEntity]);

  // minmax(0, ...) lets the Name/Code columns shrink instead of forcing the
  // row wider than its container; the RACI Role column keeps a guaranteed
  // min so the segmented control always fits, and Actions stays fixed-width
  // and visible without horizontal scrolling.
  const gridColumns =
    isManage ?
      "minmax(0, 1.6fr) minmax(0, 1fr) minmax(200px, 1.3fr) 110px 96px"
    : "minmax(0, 1.6fr) minmax(0, 1fr) minmax(180px, 1.4fr) 88px";

  // Commit the link into the RACI rows.
  const commitAdd = (id) => {
    if (rows.some((r) => Number(r[idKey]) === Number(id))) return;
    onChange([
      ...rows,
      { [idKey]: Number(id), role: null, time_allocation_percentage: null },
    ]);
  };

  const handleAdd = () => {
    if (!itemToAdd || disabled) return;
    const id = Number(itemToAdd);
    if (rows.some((r) => Number(r[idKey]) === id)) {
      setItemToAdd("");
      return;
    }

    // Resolve the (job, task) pair for the skill-match check. The item being
    // added is a task when entityType="task", otherwise a job.
    const apiJobId = isTaskEntity ? jobId : id;
    const apiTaskId = isTaskEntity ? id : taskId;

    const addedItem = itemById.get(String(id));
    const addedName = getName(addedItem) || "";
    const selfName = label || "";
    const jobName = isTaskEntity ? selfName : addedName;
    const taskName = isTaskEntity ? addedName : selfName;

    // Without both persisted ids we can't run the check — add directly.
    if (apiJobId == null || apiJobId === "" || apiTaskId == null || apiTaskId === "") {
      commitAdd(id);
      setItemToAdd("");
      return;
    }

    setSkillModal({
      open: true,
      loading: true,
      error: null,
      data: null,
      addId: id,
      jobName,
      taskName,
    });
    setItemToAdd("");

    getJobTaskSkills(apiJobId, apiTaskId)
      .then((data) => {
        setSkillModal((prev) =>
          prev.addId === id ? { ...prev, loading: false, data } : prev,
        );
      })
      .catch((err) => {
        setSkillModal((prev) =>
          prev.addId === id
            ? {
                ...prev,
                loading: false,
                error: err?.message || "Failed to check skills.",
              }
            : prev,
        );
      });
  };

  const handleSkillConfirm = () => {
    if (skillModal.addId != null) commitAdd(skillModal.addId);
    setSkillModal((prev) => ({ ...prev, open: false, addId: null }));
  };

  const handleSkillCancel = () => {
    setSkillModal((prev) => ({ ...prev, open: false, addId: null }));
  };

  const handleRemove = (id) => {
    onChange(rows.filter((r) => String(r[idKey]) !== String(id)));
  };

  const handleSetRole = (id, role) => {
    onChange(
      rows.map((r) => {
        if (String(r[idKey]) !== String(id)) return r;
        // If the current value is null or still at the previous role's default,
        // replace it with the new role's default. Manual edits are preserved.
        const prevDefault = r.role ? RACI_DEFAULT_ALLOCATION[r.role] : null;
        const isDefaultOrNull =
          r.time_allocation_percentage == null ||
          r.time_allocation_percentage === prevDefault;
        return {
          ...r,
          role,
          time_allocation_percentage: isDefaultOrNull
            ? (RACI_DEFAULT_ALLOCATION[role] ?? null)
            : r.time_allocation_percentage,
        };
      }),
    );
  };

  const handleSetAllocation = (id, value) => {
    onChange(
      rows.map((r) =>
        String(r[idKey]) === String(id)
          ? { ...r, time_allocation_percentage: value === "" ? null : Number(value) }
          : r,
      ),
    );
  };

  return (
    <div className="role-card">
      <div
        className="section-title"
        style={{ marginBottom: 12, fontWeight: 600 }}
      >
        RACI Matrix
      </div>

      {isManage && disabled && (
        <div className="raci-disabled-banner">{disabledMessage}</div>
      )}

      {isManage && (
        <div className="form-grid">
          <div className="form-group" style={{ maxWidth: 420 }}>
            <label>{addLabel}</label>
            <div style={{ display: "flex", gap: 8 }}>
              <select
                value={itemToAdd}
                onChange={(e) => setItemToAdd(e.target.value)}
                style={{ flex: 1 }}
                disabled={disabled}
              >
                <option value="">{selectPlaceholder}</option>
                {availableItems.map((it) => (
                  <option key={it[idKey]} value={it[idKey]}>
                    {getName(it)} ({getCode(it)})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="primary-btn"
                onClick={handleAdd}
                disabled={!itemToAdd || disabled}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="roles-table raci-table" style={{ marginTop: 8 }}>
        <div
          className="roles-table-header"
          style={{ gridTemplateColumns: gridColumns }}
        >
          <div className="cell">{nameColumnLabel}</div>
          <div className="cell">{codeColumnLabel}</div>
          <div className="cell">RACI Role</div>
          <div className="cell" style={{ textAlign: "center" }}>% Allocation</div>
          {isManage && (
            <div className="cell actions" style={{ textAlign: "right" }}>
              Actions
            </div>
          )}
        </div>

        {visibleRows.length === 0 ?
          <div className="no-results">{emptyLabel}</div>
        : visibleRows.map((r) => {
            const it = itemById.get(String(r[idKey]));
            return (
              <div
                key={r[idKey]}
                className={`roles-table-row${isManage && !r.role ? " raci-row-unassigned" : ""}`}
                style={{ gridTemplateColumns: gridColumns }}
              >
                <div className="cell">{getName(it) || "-"}</div>
                <div className="cell">{getCode(it) || "-"}</div>
                <div className="cell">
                  {isManage ?
                    <div className={`raci-seg${!r.role ? " raci-seg-required" : ""}`}>
                      {RACI_ROLES.map((role) => (
                        <button
                          key={role}
                          type="button"
                          className={`raci-seg-btn raci-${role.toLowerCase()} ${r.role === role ? "active" : ""}`}
                          onClick={() => handleSetRole(r[idKey], role)}
                          title={`${ROLE_META[role].label} — ${ROLE_META[role].description}`}
                        >
                          {role}
                        </button>
                      ))}
                    </div>
                  : <span
                      className={`raci-badge ${r.role ? `raci-${r.role.toLowerCase()}` : "raci-none"}`}
                    >
                      {r.role || "–"}
                    </span>
                  }
                </div>
                <div className="cell" style={{ textAlign: "center" }}>
                  {isManage ? (
                    <div className="raci-alloc-wrap">
                      <input
                        type="number"
                        className="raci-alloc-input"
                        min={0}
                        max={100}
                        value={r.time_allocation_percentage ?? ""}
                        onChange={(e) => handleSetAllocation(r[idKey], e.target.value)}
                        placeholder="—"
                        disabled={!r.role}
                        title={!r.role ? "Select a RACI role first" : undefined}
                      />
                      <span className="raci-alloc-suffix">%</span>
                    </div>
                  ) : (
                    <span className="raci-alloc-display">
                      {r.time_allocation_percentage != null ? `${r.time_allocation_percentage}%` : "—"}
                    </span>
                  )}
                </div>
                {isManage && (
                  <div className="cell actions" style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className="danger-btn sm"
                      onClick={() => handleRemove(r[idKey])}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            );
          })
        }
      </div>

      {visibleRows.length > 0 && (
        <div className={`raci-banner ${validation.valid ? "valid" : "invalid"}`}>
          {validation.valid ?
            "✓ RACI assignments are complete."
          : validation.messages.join(" ")}
        </div>
      )}

      {visibleRows.length > 0 && (
        <RaciSummaryTable
          rows={rows}
          itemById={itemById}
          idKey={idKey}
          getName={getName}
          isTaskEntity={isTaskEntity}
          label={label}
        />
      )}

      <div className="raci-legend">
        {RACI_ROLES.map((role) => (
          <div className="raci-legend-item" key={role}>
            <span className={`raci-legend-badge raci-${role.toLowerCase()}`}>
              {role}
            </span>
            {ROLE_META[role].label} &mdash; {ROLE_META[role].description}
          </div>
        ))}
      </div>

      <SkillMatchModal
        open={skillModal.open}
        loading={skillModal.loading}
        error={skillModal.error}
        data={skillModal.data}
        jobName={skillModal.jobName}
        taskName={skillModal.taskName}
        onConfirm={handleSkillConfirm}
        onCancel={handleSkillCancel}
      />
    </div>
  );
};

export default RaciMatrix;
